import { faFilter, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import Button from "components/base/Button";
import SearchBox from "components/common/SearchBox";
import TodoListItem from "components/modules/project-management/todo-list/TodoListItem";
import axiosInstance from "helpers/api";
import { useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { useForm, SubmitHandler } from "react-hook-form";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import ConfirmDeleteModal from "../common-components/ConfirmDeleteModal";
import { getISODate } from "helpers/utils";
// import DealDetailsTaskEdit from "./DealDetailsTaskEdit";

type Inputs = {
  task: string;
  priority: string;
  due_date: Date;
  task_status: string;
};

const TaskPriority = ["Low", "Medium", "High"];
const TaskStatus = ["Pending", "In Progress", "Completed"];

const DealDetailsTask = ({ tasks, handleSearchTask }: any) => {
  const [taskItems, setTaskItems] = useState<any>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [setTaskId, setSetTaskId] = useState(null);

  let { id } = useParams();

  const {
    register: registerCreate,
    handleSubmit: handleSubmitCreate,
    reset: resetCreate,
    formState: { errors: errorsCreate },
  } = useForm<Inputs>();

  const {
    register: registerEdit,
    handleSubmit: handleSubmitEdit,
    reset: resetEdit,
    setValue,
    formState: { errors: errorsEdit },
  } = useForm<Inputs>();

  const onSubmitCreate: SubmitHandler<Inputs> = async (data) => {
    try {
      const response = await axiosInstance.post(`/deals/${id}/tasks`, {
        task: data.task,
        status: data.task_status,
        priority: data.priority,
        due_date: getISODate(data.due_date),
      });
      toast.success(response.data.message);
      resetCreate();
      setTaskItems(response.data.data.tasks);
      setShowAddForm(false);
    } catch (error: any) {
      toast.error(error.response.data.message);
    }
  };

  const onSubmitEdit: SubmitHandler<Inputs> = async (data) => {
    try {
      const response = await axiosInstance.patch(
        `/deals/${id}/tasks/${setTaskId}`,
        {
          task: data.task,
          status: data.task_status,
          priority: data.priority,
          due_date: getISODate(data.due_date),
        }
      );
      toast.success(response.data.message);
      resetEdit();
      setTaskItems(response.data.data.tasks);
      setShowEditForm(false);
    } catch (error: any) {
      toast.error(error.response.data.message);
    }
  };

  useEffect(() => {
    setTaskItems(tasks);
  }, [tasks]);

  const handleToggleTask = async (task: any) => {
    try {
      const response = await axiosInstance.patch(
        `/deals/${id}/tasks/${task.id}`,
        {
          is_completed: !task.is_completed,
          status: task.status === "Completed" ? "Pending" : "Completed",
        }
      );
      toast.success(response.data.message);
      resetCreate();
      setTaskItems(response.data.data.tasks);
    } catch (error: any) {
      toast.error(error.response.data.message);
    }
  };

  const handleEditTask = (id: any) => {
    const filterData = taskItems?.find((taskItem: any) => taskItem.id === id);
    setShowEditForm(true);
    setShowAddForm(false);

    setValue("task", filterData.task);
    setValue("priority", filterData.priority);
    setValue("due_date", filterData?.due_date?.split("T")[0]);
    setValue("task_status", filterData.status);
    setSetTaskId(filterData.id);
  };

  const confirmDeleteTask = (id: any) => {
    setModalShow(true);
    setSetTaskId(id);
    setShowEditForm(false);
    setShowAddForm(false);
  };

  const handleDeleteTask = async () => {
    try {
      const response = await axiosInstance.delete(
        `/deals/${id}/tasks/${setTaskId}`
      );
      toast.success(response.data.message);
      setTaskItems(taskItems.filter((item: any) => item.id !== setTaskId));
    } catch (error: any) {
      toast.error(error.response.data.message);
    }
    setModalShow(false);
  };

  const handleThumbsDown = async (task: any) => {
    try {
      const response = await axiosInstance.patch(
        `/deals/${id}/tasks/${task.id}`,
        {
          is_liked: !task.is_liked,
        }
      );
      toast.success(response.data.message);
      resetCreate();
      setTaskItems(response.data.data.tasks);
    } catch (error: any) {
      toast.error(error.response.data.message);
    }
  };

  return (
    <>
      <h2 className="mb-4">Tasks</h2>
      <Row className="align-items-center g-0 mb-3">
        <Col sm="auto">
          <SearchBox
            placeholder="Search tasks"
            className="w-100 mb-2 mb-sm-0"
            style={{ maxWidth: "30rem" }}
            onChange={handleSearchTask}
          />
        </Col>
        <Col xs="auto">
          <div className="d-flex ms-sm-3">
            <p className="mb-0 fs-9 text-body-tertiary fw-bold">
              <FontAwesomeIcon
                icon={faFilter}
                className="me-1 fw-extra-bold fs-10"
              />
              {taskItems?.length} tasks
            </p>
          </div>
        </Col>
      </Row>
      {taskItems?.map((task: any, index: any) => (
        <TodoListItem
          key={task.id}
          todo={task}
          className={classNames("border-translucent", {
            "border-top": index === 0,
            "border-bottom-0": index === tasks.length - 1,
          })}
          fullLayoutBreakpoints={["lg"]}
          onClick={() => handleToggleTask(task)}
          handleEdit={() => handleEditTask(task.id)}
          handleDelete={() => confirmDeleteTask(task.id)}
          handleThumbsDown={() => handleThumbsDown(task)}
        />
      ))}

      {showAddForm && (
        <div className="my-5">
          <h4>Add Task</h4>
          <Form onSubmit={handleSubmitCreate(onSubmitCreate)}>
            <Row className="row g-3">
              <Col md="12">
                <Form.Label>
                  Task Name <span className="text-danger"> *</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  {...registerCreate("task", { required: true })}
                />
                {errorsCreate.task && (
                  <p className="text-danger">Task Name is required</p>
                )}
              </Col>
              <Col md="4">
                <Form.Label>Priority</Form.Label>
                <Form.Select {...registerCreate("priority")}>
                  <option value="">Select Priority</option>
                  {TaskPriority.map((item, i) => (
                    <option key={i} value={item}>
                      {item}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md="4">
                <Form.Label>Status</Form.Label>
                <Form.Select {...registerCreate("task_status")}>
                  <option value="">Select Status</option>
                  {TaskStatus.map((item, i) => (
                    <option key={i} value={item}>
                      {item}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md="4">
                <Form.Label>Due Date</Form.Label>
                <Form.Control type="date" {...registerCreate("due_date")} />
              </Col>
            </Row>
            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button variant="danger" onClick={() => setShowAddForm(false)}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Create Task
              </Button>
            </div>
          </Form>
        </div>
      )}

      {!showAddForm && (
        <Button
          variant="link"
          className="p-0 mt-2"
          startIcon={<FontAwesomeIcon icon={faPlus} />}
          onClick={() => {
            setShowAddForm(true);
            setShowEditForm(false);
          }}
        >
          Add new task
        </Button>
      )}

      <ConfirmDeleteModal
        modalShow={modalShow}
        setModalShow={setModalShow}
        type="Task"
        handleDelete={handleDeleteTask}
      />

      {showEditForm && (
        <div className="my-5">
          <h4>Edit Task</h4>
          <Form onSubmit={handleSubmitEdit(onSubmitEdit)}>
            <Row className="row g-3">
              <Col md="12">
                <Form.Label>
                  Task Name <span className="text-danger"> *</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  {...registerEdit("task", { required: true })}
                />
                {errorsEdit.task && (
                  <p className="text-danger">Task Name is required</p>
                )}
              </Col>
              <Col md="4">
                <Form.Label>Priority</Form.Label>
                <Form.Select {...registerEdit("priority")}>
                  <option value="">Select Priority</option>
                  {TaskPriority.map((item, i) => (
                    <option key={i} value={item}>
                      {item}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md="4">
                <Form.Label>Status</Form.Label>
                <Form.Select {...registerEdit("task_status")}>
                  <option value="">Select Status</option>
                  {TaskStatus.map((item, i) => (
                    <option key={i} value={item}>
                      {item}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md="4">
                <Form.Label>Due Date</Form.Label>
                <Form.Control type="date" {...registerEdit("due_date")} />
              </Col>
            </Row>
            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button variant="danger" onClick={() => setShowEditForm(false)}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Update Task
              </Button>
            </div>
          </Form>
        </div>
      )}
    </>
  );
};

export default DealDetailsTask;
